import React, { useState } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { applicantsAPI, formatApiErrorDetail } from '../../lib/api';
import { Shield, CheckCircle, Star, Clock, User, Envelope, Phone, MapPin, Briefcase, Cross, Users, FileText } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const COUNTRIES = [
  'Antigua and Barbuda', 'Bahamas', 'Barbados', 'Belize', 'Dominica', 
  'Dominican Republic', 'Grenada', 'Guyana', 'Haiti', 'Jamaica',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Suriname', 'Trinidad and Tobago', 'Other Caribbean', 'United States', 
  'United Kingdom', 'Canada', 'Other'
];

const DENOMINATIONS = [
  'Roman Catholic', 'Anglican/Episcopal', 'Methodist', 'Baptist', 
  'Pentecostal', 'Presbyterian', 'Lutheran', 'Orthodox', 'Other Christian'
];

const HOW_HEARD = [
  'Current Member', 'Family/Friend', 'Church', 'Social Media', 
  'Website', 'Event', 'News/Media', 'Other'
];

const MembershipPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    date_of_birth: '',
    nationality: '',
    occupation: '',
    employer: '',
    christian_denomination: '',
    church_name: '',
    baptized: null,
    confirmed: null,
    motivation: '',
    community_service: '',
    professional_background: '',
    reference_name_1: '',
    reference_email_1: '',
    reference_phone_1: '',
    reference_name_2: '',
    reference_email_2: '',
    reference_phone_2: '',
    sponsor_name: '',
    how_did_you_hear: '',
    agree_to_principles: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_to_principles) {
      toast.error('You must agree to uphold the principles of the Order');
      return;
    }

    setSubmitting(true);
    try {
      await applicantsAPI.create({
        ...formData,
        source: 'website_application'
      });
      setSubmitted(true);
      toast.success('Application submitted successfully');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.full_name && formData.email && formData.phone && formData.country;
      case 2:
        return formData.christian_denomination && formData.baptized !== null;
      case 3:
        return formData.motivation && formData.reference_name_1 && formData.reference_email_1;
      case 4:
        return formData.agree_to_principles;
      default:
        return true;
    }
  };

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-20 bg-[#1A1614] text-center" data-testid="membership-header">
        <p className="text-overline mb-4">Join the Order</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
          Membership Application
        </h1>
        <p className="text-[#8A847A] max-w-2xl mx-auto px-4">
          Begin your journey to becoming a Knight or Dame of the Grand Priory of the Caribbean
        </p>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-white" data-testid="membership-requirements">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">
            Requirements for Membership
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: 'Christian Faith', desc: 'Must be a practicing Christian of good standing in their faith community.' },
              { icon: CheckCircle, title: 'Good Character', desc: 'Must demonstrate integrity, honor, and moral uprightness.' },
              { icon: Star, title: 'Civic Contribution', desc: 'Must have a record of service to community and society.' },
              { icon: Clock, title: 'Commitment', desc: 'Must be willing to actively participate in Order activities.' },
            ].map(req => {
              const Icon = req.icon;
              return (
                <div key={req.title} className="flex gap-4 p-6 bg-[#F5F2EB] border border-[#D8D2C4]">
                  <Icon size={24} className="text-[#7B1818] flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg text-[#1A1614] mb-1">{req.title}</h3>
                    <p className="text-sm text-[#4A453E]">{req.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-[#F5F2EB]" data-testid="membership-process">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">
            Application Process
          </h2>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#D8D2C4]" />
            <div className="space-y-8">
              {[
                { step: 1, title: 'Submit Application', desc: 'Complete the comprehensive application form below with all required information.' },
                { step: 2, title: 'Initial Review', desc: 'The Membership Committee reviews your application and verifies references.' },
                { step: 3, title: 'Interview', desc: 'Meet with Order officers to discuss your interest and commitment.' },
                { step: 4, title: 'Background Check', desc: 'Standard verification of character and standing in the community.' },
                { step: 5, title: 'Approval', desc: 'Upon approval by the Grand Chapter, receive notification of acceptance.' },
                { step: 6, title: 'Investiture', desc: 'Take the Postulant Oath and be invested as a Novice of the Order.' },
              ].map(item => (
                <div key={item.step} className="relative pl-16">
                  <div className="absolute left-0 w-12 h-12 bg-[#7B1818] text-white flex items-center justify-center font-heading text-xl">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-xl text-[#1A1614] mb-1">{item.title}</h3>
                  <p className="text-[#4A453E]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-white" data-testid="membership-form-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-2 text-center">
            Application Form
          </h2>
          <p className="text-center text-[#8A847A] mb-8">
            Please complete all sections of this application. Fields marked with * are required.
          </p>

          {submitted ? (
            <div className="text-center p-12 bg-[#F5F2EB] border border-[#D8D2C4]" data-testid="submission-success">
              <CheckCircle size={64} className="text-green-600 mx-auto mb-6" />
              <h3 className="font-heading text-3xl text-[#1A1614] mb-4">Application Received</h3>
              <p className="text-[#4A453E] text-lg mb-4">
                Thank you for your interest in joining the Grand Priory of the Caribbean.
              </p>
              <p className="text-[#8A847A]">
                Your application has been submitted and is now under review. A member of our Membership Committee
                will contact you within 7-14 business days to discuss the next steps.
              </p>
              <div className="mt-8 p-4 bg-white border border-[#D8D2C4] inline-block">
                <p className="text-sm text-[#8A847A]">Application Reference</p>
                <p className="font-heading text-[#1A1614]">{formData.email}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="membership-application-form">
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  {[1, 2, 3, 4].map(step => (
                    <div 
                      key={step}
                      className={`flex items-center ${step < 4 ? 'flex-1' : ''}`}
                    >
                      <div 
                        className={`w-10 h-10 flex items-center justify-center font-heading text-sm
                          ${currentStep === step ? 'bg-[#7B1818] text-white' : 
                            currentStep > step ? 'bg-[#C5A059] text-white' : 'bg-[#D8D2C4] text-[#8A847A]'}`}
                      >
                        {step}
                      </div>
                      {step < 4 && (
                        <div className={`flex-1 h-1 mx-2 ${currentStep > step ? 'bg-[#C5A059]' : 'bg-[#D8D2C4]'}`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-[#8A847A]">
                  <span>Personal</span>
                  <span>Faith</span>
                  <span>Background</span>
                  <span>Review</span>
                </div>
              </div>

              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6" data-testid="step-1">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#D8D2C4]">
                    <User size={20} className="text-[#7B1818]" />
                    <h3 className="font-heading text-xl text-[#1A1614]">Personal Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Full Legal Name *</label>
                      <Input
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        placeholder="As it appears on official documents"
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Date of Birth *</label>
                      <Input
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        required
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-dob"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Email Address *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Phone Number *</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+1 (XXX) XXX-XXXX"
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1614] mb-2">Street Address *</label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="rounded-none border-[#D8D2C4]"
                      data-testid="input-address"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">City *</label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Country *</label>
                      <Select value={formData.country} onValueChange={(v) => handleSelectChange('country', v)}>
                        <SelectTrigger className="rounded-none border-[#D8D2C4]" data-testid="select-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Nationality</label>
                      <Input
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleChange}
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-nationality"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Occupation *</label>
                      <Input
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleChange}
                        required
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-occupation"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1614] mb-2">Employer/Organization</label>
                    <Input
                      name="employer"
                      value={formData.employer}
                      onChange={handleChange}
                      className="rounded-none border-[#D8D2C4]"
                      data-testid="input-employer"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Faith & Christian Background */}
              {currentStep === 2 && (
                <div className="space-y-6" data-testid="step-2">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#D8D2C4]">
                    <Cross size={20} className="text-[#7B1818]" />
                    <h3 className="font-heading text-xl text-[#1A1614]">Faith & Christian Background</h3>
                  </div>

                  <div className="bg-[#F5F2EB] p-4 border border-[#D8D2C4] mb-6">
                    <p className="text-sm text-[#4A453E]">
                      The Sovereign Military Order of the Temple of Jerusalem requires all members to be practicing Christians.
                      Please provide information about your Christian faith and community.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Christian Denomination *</label>
                      <Select 
                        value={formData.christian_denomination} 
                        onValueChange={(v) => handleSelectChange('christian_denomination', v)}
                      >
                        <SelectTrigger className="rounded-none border-[#D8D2C4]" data-testid="select-denomination">
                          <SelectValue placeholder="Select denomination" />
                        </SelectTrigger>
                        <SelectContent>
                          {DENOMINATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Church/Parish Name</label>
                      <Input
                        name="church_name"
                        value={formData.church_name}
                        onChange={handleChange}
                        placeholder="Where do you worship?"
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-church"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Have you been Baptized? *</label>
                      <Select 
                        value={formData.baptized === null ? '' : formData.baptized ? 'yes' : 'no'} 
                        onValueChange={(v) => handleSelectChange('baptized', v === 'yes')}
                      >
                        <SelectTrigger className="rounded-none border-[#D8D2C4]" data-testid="select-baptized">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">Have you been Confirmed?</label>
                      <Select 
                        value={formData.confirmed === null ? '' : formData.confirmed ? 'yes' : 'no'} 
                        onValueChange={(v) => handleSelectChange('confirmed', v === 'yes')}
                      >
                        <SelectTrigger className="rounded-none border-[#D8D2C4]" data-testid="select-confirmed">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="na">N/A (denomination does not practice)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Background & References */}
              {currentStep === 3 && (
                <div className="space-y-6" data-testid="step-3">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#D8D2C4]">
                    <Briefcase size={20} className="text-[#7B1818]" />
                    <h3 className="font-heading text-xl text-[#1A1614]">Background & References</h3>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1614] mb-2">
                      Why do you wish to become a member of the Order? *
                    </label>
                    <Textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Please describe your motivations for seeking membership..."
                      className="rounded-none border-[#D8D2C4]"
                      data-testid="input-motivation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1614] mb-2">
                      Community Service & Charitable Activities
                    </label>
                    <Textarea
                      name="community_service"
                      value={formData.community_service}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Describe your involvement in community service, charitable work, or volunteer activities..."
                      className="rounded-none border-[#D8D2C4]"
                      data-testid="input-community"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1614] mb-2">
                      Professional Background
                    </label>
                    <Textarea
                      name="professional_background"
                      value={formData.professional_background}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Brief summary of your professional career and achievements..."
                      className="rounded-none border-[#D8D2C4]"
                      data-testid="input-professional"
                    />
                  </div>

                  {/* References */}
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#D8D2C4]">
                      <Users size={20} className="text-[#7B1818]" />
                      <h3 className="font-heading text-xl text-[#1A1614]">Character References</h3>
                    </div>
                    <p className="text-sm text-[#8A847A] mb-4">
                      Please provide two character references who can speak to your moral standing and community involvement.
                      References should not be family members.
                    </p>

                    <div className="space-y-6">
                      <div className="p-4 bg-[#F5F2EB] border border-[#D8D2C4]">
                        <h4 className="font-medium text-[#1A1614] mb-3">Reference 1 *</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Full Name *</label>
                            <Input
                              name="reference_name_1"
                              value={formData.reference_name_1}
                              onChange={handleChange}
                              required
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref1-name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Email *</label>
                            <Input
                              name="reference_email_1"
                              type="email"
                              value={formData.reference_email_1}
                              onChange={handleChange}
                              required
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref1-email"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Phone</label>
                            <Input
                              name="reference_phone_1"
                              value={formData.reference_phone_1}
                              onChange={handleChange}
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref1-phone"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-[#F5F2EB] border border-[#D8D2C4]">
                        <h4 className="font-medium text-[#1A1614] mb-3">Reference 2</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Full Name</label>
                            <Input
                              name="reference_name_2"
                              value={formData.reference_name_2}
                              onChange={handleChange}
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref2-name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Email</label>
                            <Input
                              name="reference_email_2"
                              type="email"
                              value={formData.reference_email_2}
                              onChange={handleChange}
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref2-email"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-[#8A847A] mb-1">Phone</label>
                            <Input
                              name="reference_phone_2"
                              value={formData.reference_phone_2}
                              onChange={handleChange}
                              className="rounded-none border-[#D8D2C4]"
                              data-testid="input-ref2-phone"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">
                        Do you know a current member who can sponsor you?
                      </label>
                      <Input
                        name="sponsor_name"
                        value={formData.sponsor_name}
                        onChange={handleChange}
                        placeholder="Member's name (optional)"
                        className="rounded-none border-[#D8D2C4]"
                        data-testid="input-sponsor"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1614] mb-2">
                        How did you hear about us?
                      </label>
                      <Select 
                        value={formData.how_did_you_hear} 
                        onValueChange={(v) => handleSelectChange('how_did_you_hear', v)}
                      >
                        <SelectTrigger className="rounded-none border-[#D8D2C4]" data-testid="select-how-heard">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {HOW_HEARD.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Review & Submit */}
              {currentStep === 4 && (
                <div className="space-y-6" data-testid="step-4">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-[#D8D2C4]">
                    <FileText size={20} className="text-[#7B1818]" />
                    <h3 className="font-heading text-xl text-[#1A1614]">Review & Submit</h3>
                  </div>

                  <div className="bg-[#F5F2EB] p-6 border border-[#D8D2C4]">
                    <h4 className="font-heading text-lg text-[#1A1614] mb-4">Application Summary</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                      <div>
                        <p className="text-[#8A847A]">Full Name</p>
                        <p className="font-medium text-[#1A1614]">{formData.full_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#8A847A]">Email</p>
                        <p className="font-medium text-[#1A1614]">{formData.email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#8A847A]">Phone</p>
                        <p className="font-medium text-[#1A1614]">{formData.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#8A847A]">Location</p>
                        <p className="font-medium text-[#1A1614]">{formData.city}, {formData.country}</p>
                      </div>
                      <div>
                        <p className="text-[#8A847A]">Occupation</p>
                        <p className="font-medium text-[#1A1614]">{formData.occupation || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#8A847A]">Denomination</p>
                        <p className="font-medium text-[#1A1614]">{formData.christian_denomination || '-'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-[#8A847A]">Primary Reference</p>
                        <p className="font-medium text-[#1A1614]">{formData.reference_name_1} ({formData.reference_email_1})</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#7B1818]/5 p-6 border border-[#7B1818]/20">
                    <h4 className="font-heading text-lg text-[#1A1614] mb-4">Declaration</h4>
                    <p className="text-sm text-[#4A453E] mb-4">
                      By submitting this application, I solemnly declare that:
                    </p>
                    <ul className="text-sm text-[#4A453E] space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#7B1818] flex-shrink-0 mt-0.5" />
                        <span>All information provided in this application is true and accurate to the best of my knowledge.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#7B1818] flex-shrink-0 mt-0.5" />
                        <span>I am a practicing Christian and will uphold Christian values and teachings.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#7B1818] flex-shrink-0 mt-0.5" />
                        <span>I understand and agree to uphold the principles, traditions, and charitable mission of the Order.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#7B1818] flex-shrink-0 mt-0.5" />
                        <span>I commit to actively participating in the activities and duties of the Order if accepted.</span>
                      </li>
                    </ul>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agree_to_principles"
                        checked={formData.agree_to_principles}
                        onChange={handleChange}
                        className="w-5 h-5 mt-0.5 text-[#7B1818] border-[#D8D2C4] rounded focus:ring-[#C5A059]"
                        data-testid="checkbox-agree"
                      />
                      <span className="text-sm text-[#1A1614] font-medium">
                        I have read and agree to the above declaration and consent to the processing of my personal data
                        for the purpose of this membership application. *
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-[#D8D2C4]">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="rounded-none border-[#D8D2C4]"
                  >
                    Previous
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!isStepValid(currentStep)}
                    className="bg-[#7B1818] hover:bg-[#5C1212] text-white rounded-none"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitting || !formData.agree_to_principles}
                    className="bg-[#7B1818] hover:bg-[#5C1212] text-white rounded-none px-8"
                    data-testid="submit-application-button"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                )}
              </div>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default MembershipPage;
